import { defineStore } from "pinia";
import { api } from "@/helpers/api.js";

import { getSubcategoryKey } from "@/helpers/subcategories";
import { useTicket } from "@/helpers/tickets.js";
import { useUserStore } from "@/store/user.js";

export const useEventStore = defineStore("event", {
  state: () => ({
    event: null,
    tickets: [],
    categories: [],
    eventNotFound: false,
    loading: false,
    showAllTickets: true,
    filterTicketsWithoutVat: false,
    groupRequestModal: false,
    selectedTicket: null,
    selectedCategoryId: null,
    isLoadingCategories: false,
    isLoadingSubcategoryTickets: false,
    isLoadingTickets: false,
    isLoadingEvent: false,
  }),
  getters: {
    isSingleDayEvent(state) {
      const numberOfDays =
        (Date.parse(state.event.date_stop + "Z") - Date.parse(state.event.date_start + "Z")) / (1000 * 3600 * 24);
      return numberOfDays <= 1;
    },
    isLoading(state) {
      return (
        state.isLoadingCategories || state.isLoadingEvent || state.isLoadingSubcategoryTickets || state.isLoadingTickets
      );
    },
  },
  actions: {
    async fetchSubcategoryTickets(ticket) {
      this.isLoadingSubcategoryTickets = true;
      const params = new URLSearchParams({
        category_id: ticket.category_id,
        event_id: ticket.event_id,
        ticket_status: "available",
        stock: "ge:1",
        sorting: "category_name:asc,ticket_id:asc,face_value:asc",
      });
      const req = api.get("/tickets?" + params);
      const res = await req;
      const subcategoryKey = getSubcategoryKey(ticket);
      const tickets = res.data.tickets
        .filter((ticket) => ticket.category_id !== "0")
        .filter((ticket) => getSubcategoryKey(ticket) === subcategoryKey);
      this.isLoadingSubcategoryTickets = false;
      return tickets.map(useTicket);
    },
    async fetchCategories(eventId) {
      this.isLoadingCategories = true;
      const categoryParams = new URLSearchParams({
        event_id: eventId,
        page_size: 9999,
      });
      const res = await api.get("/categories?" + categoryParams);
      const categories = res.data.categories;
      categories.sort((a, b) => a.category_name?.localeCompare(b.category_name));
      this.isLoadingCategories = false;
      return (this.categories = categories);
    },
    async fetchEvent(eventId) {
      this.isLoadingEvent = true;
      try {
        const res = await api.cache("/events/" + eventId);
        this.eventNotFound = false;
        return (this.event = res.data);
      } catch (err) {
        this.eventNotFound = true;
        throw err;
      } finally {
        this.isLoadingEvent = false;
      }
    },
    async getEvent(eventId) {
      const req = api.cache("/events/" + eventId);
      const res = await req;
      return res.data;
    },
    async getTicket(ticketId) {
      const req = api.get("/tickets/" + ticketId + "?show_deleted=true");
      const res = await req;
      return res.data;
    },
    getCategory(categoryId) {
      return api.get("/categories/" + categoryId).then((res) => {
        return res.data;
      });
    },
    async getCategories(categoryIds) {
      const categoryParams = new URLSearchParams({
        category_id: categoryIds,
        page_size: 25,
      });
      return await api.get("/categories?" + categoryParams);
    },
    async fetchAllTicketsOnCategoryId(categoryIds) {
      const ticketParams = new URLSearchParams({
        page_size: 9999,
        ticket_status: "available",
        face_value: "gt:0",
        sorting: "ticket_validfrom:asc",
        stock: "ge:1",
        category_id: categoryIds,
      });
      const req = api.get("/tickets?" + ticketParams);
      const res = await req;
      return res.data.tickets;
    },

    fetchCategoryImages(categoryId) {
      return api.get(`/mediacontent/category/${categoryId}/image`).then((res) => {
        return res.data.mediacontents;
      });
    },

    fetchCategoryVideos(categoryId) {
      return api.get(`/mediacontent/category/${categoryId}/video`).then((res) => {
        return res.data.mediacontents;
      });
    },

    groupRequest(payload) {
      return api.post("/grouprequests", payload);
    },

    requestMail(payload) {
      return api
        .post("/support/email", payload)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          throw error;
        });
    },

    getTournamentById(id) {
      const getUri = "/tournaments";
      return api.get(`${getUri}/${id}`).then((res) => {
        return res.data;
      });
    },

    fetchPreferredLanguageEvent(eventId, lang) {
      const params = new URLSearchParams({
        accept_language: lang,
      });
      return api.get(`/events/${eventId}?` + params).then((res) => {
        return res.data;
      });
    },
    async resetVatFilter() {
      // we want some suppliers/distributors to have the vat filter on by default
      const suppliersDistributorsVATFilterOn = [
        "65bf6f6284ae4ba880ac4722375a91ea_spp", // TD Entertainment C.V.
        "5996d72153ad41308a97625e7fa03ff5_dst", // A. Ostermann GMBH
        "fsd69f4k5d9a143369e63tuio0005cp8_spp", // Motorsport tickets
        "9fad6b504890402baab2d6fb7d1878ca_dst", // Book Events
        "2b152897e6574139a3c35284044c5057_dst", // Platinium Group SAM
        "c1771cb0e8fb4383b7107a88116d8542_spp", // Grand Prix Tickets Ges.m.b.H.
        "cde198b77cd74155a6bf96dbbb518135_spp", // GPtickets s.r.o.
        "ef30a9c6b3c447829615c92cda06d148_spp", // Across Spain
        "c2f753f63f834cfa83f668dca59115e9_dst", // Passion Events
        "b599521988d5442b96ff923be68c2a17_dst", // All Sport srl
        "e9738c13254a4e7ab9ee43133b91d31e_dst", // Carstensen Import - Export Handelsgesellschaft mbH
      ];
      const userStore = useUserStore();

      await userStore.authCompleted;

      if (
        suppliersDistributorsVATFilterOn.includes(userStore.supplier_id) ||
        suppliersDistributorsVATFilterOn.includes(userStore.distributor_id)
      ) {
        this.filterTicketsWithoutVat = true;
      } else {
        this.filterTicketsWithoutVat = false;
      }
    },
  },
});
